/**
 * PhaseExplainer — Educational card for each treatment phase.
 *
 * Renders phase-appropriate content based on currentPhase.
 * Collapsible "What to expect" section.
 */

import React, { useState } from "react";
import { View, Pressable } from "react-native";
import { Text } from "@src/packages/ui/Text/Text";
import { BookOpen, ChevronDown, ChevronUp, Phone } from "lucide-react-native";
import type { TreatmentPhaseName } from "../treatment-phases";
import { getPhaseContent } from "./phaseContent";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

interface PhaseExplainerProps {
  phase?: TreatmentPhaseName;
}

export function PhaseExplainer({ phase: phaseProp }: PhaseExplainerProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("PhaseExplainer");
  const phase = phaseProp ?? "getting_started";
  const [expanded, setExpanded] = useState(false);

  const content = getPhaseContent(phase, t);
  if (!content) return null;

  return (
    <View className="rounded-2xl border border-white/10 bg-white/5">
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(`What to expect: ${content.title}`),
            "what_to_expect_toggle",
          );
          setExpanded(!expanded);
        }}
        className="flex-row items-center justify-between px-4 py-3"
      >
        <View className="flex-row items-center gap-2">
          <BookOpen size={16} color="#C4B5FD" />
          <Text className="text-sm font-medium text-white">
            What to expect: {content.title}
          </Text>
        </View>
        {expanded ? (
          <ChevronUp size={16} color="rgba(255,255,255,0.3)" />
        ) : (
          <ChevronDown size={16} color="rgba(255,255,255,0.3)" />
        )}
      </Pressable>

      {expanded && (
        <View className="gap-4 border-t border-white/5 px-4 pb-4 pt-3">
          {/* Description */}
          <Text className="text-sm leading-5 text-slate-300">
            {content.description}
          </Text>

          {/* Key Facts */}
          {content.keyFacts.length > 0 && (
            <View className="gap-1.5">
              <Text className="text-xs font-medium text-slate-400">
                Key Facts
              </Text>
              {content.keyFacts.map((fact, i) => (
                <Text key={i} className="text-xs text-slate-400">
                  • {fact}
                </Text>
              ))}
            </View>
          )}

          {/* Duration */}
          {content.typicalDuration && (
            <View className="rounded-xl bg-white/5 px-3 py-2">
              <Text className="text-xs text-slate-400">
                Typical duration:{" "}
                <Text className="font-medium text-white">
                  {content.typicalDuration}
                </Text>
              </Text>
            </View>
          )}

          {/* When to Call */}
          {content.whenToCall.length > 0 && (
            <View className="rounded-xl border border-amber-500/15 bg-amber-500/5 px-3 py-2">
              <View className="mb-1 flex-row items-center gap-1.5">
                <Phone size={11} color="#FBBF24" />
                <Text className="text-xs font-medium text-amber-400">
                  Call your doctor if:
                </Text>
              </View>
              {content.whenToCall.map((reason, i) => (
                <Text key={i} className="text-xs text-slate-400">
                  • {reason}
                </Text>
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  );
}
